import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { DateCell } from '@/components/data-table/cells';
import { CopyButton } from '@/components/ui/copy-button';
import { StatusChip } from '@/components/ui/status-chip';
import { abuseSubjectPath, formatEventType, getWeightBand, SUBJECT_TYPE_SINGULAR, WEIGHT_BAND_CHIP_VARIANTS } from '@/features/abuse/abuse-meta';
import type { AbuseEvent } from '@/services/api/abuse';
import { shortUuid } from '@/utils/string';

const ActorCell = ({ event }: { event: AbuseEvent }) => (
  <span className="inline-flex items-center gap-1 font-mono text-xs">
    <Link
      className="underline underline-offset-2"
      onClick={(clickEvent) => clickEvent.stopPropagation()}
      title={`${SUBJECT_TYPE_SINGULAR[event.actorType]} ${event.actorId}`}
      to={abuseSubjectPath({ subjectType: event.actorType, subjectId: event.actorId })}
    >
      {shortUuid(event.actorId)}
    </Link>
    <CopyButton label={`Copy ${event.actorId}`} value={event.actorId} />
  </span>
);
ActorCell.displayName = 'ActorCell';

export const EVENT_COLUMNS: ColumnDef<AbuseEvent, unknown>[] = [
  { accessorKey: 'occurredAt', header: 'Occurred', enableSorting: false, cell: ({ row }) => <DateCell value={row.original.occurredAt} /> },
  {
    accessorKey: 'eventType',
    header: 'Type',
    enableSorting: false,
    cell: ({ row }) => (
      <StatusChip className="px-2 py-0.5 text-xs" variant={WEIGHT_BAND_CHIP_VARIANTS[getWeightBand(row.original.weight)]}>
        {formatEventType(row.original.eventType)}
      </StatusChip>
    ),
  },
  { accessorKey: 'weight', header: 'Weight', enableSorting: false, cell: ({ row }) => <span className="tabular-nums">{row.original.weight}</span> },
  { accessorKey: 'service', header: 'Service', enableSorting: false },
  {
    accessorKey: 'endpoint',
    header: 'Endpoint',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="block max-w-xs truncate font-mono text-xs" title={row.original.endpoint}>
        {row.original.endpoint}
      </span>
    ),
  },
  { accessorKey: 'actorId', header: 'Actor', enableSorting: false, cell: ({ row }) => <ActorCell event={row.original} /> },
  { accessorKey: 'ip', header: 'IP', enableSorting: false, cell: ({ row }) => <span className="font-mono text-xs">{row.original.ip}</span> },
];
